import axios from "axios"
import config from "../config"
import apiConfig from "../api.config"
import SkillsModel from "../model/skills.model"
import { UploadResumeModel } from "../model/upload-resume.model"
import instanceAPI from "./instances/api.instance"
import { SummaryResumeModel } from "../model/summary-resume.model"
import tokenService from "./token.service"
import instanceQuizAPI from "./instances/quiz.instance"


export default function APIService() {

    return {
        async getSkills() {

            return axios.get(config.skills.url + apiConfig.skills.skill, {
                headers: {
                    'Content-Type': "application/json",
                    "x-api-key": config.skills.x_api_key
                }
            }).then(res => res).catch(err => console.log(err))
        },

    }
}
export async function createCompany(company_id) {
    return instanceAPI.post(config.api.url + apiConfig.base.company + "/create-company", { company_id: company_id });
}
export async function getCompany(company_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.company + "/" + company_id);
}

/**
 * Upload a fresh company logo file to Firebase Storage and persist the resulting
 * URL on the Firestore interview_company doc. Used by the admin layout when its
 * MySA-derived logo doesn't match what the public flow renders.
 */
export async function uploadCompanyLogo(company_id: string, file: File) {
    const formData = new FormData();
    formData.append("logo", file, file.name);
    return instanceAPI.post(
        config.api.url + apiConfig.base.company + "/" + company_id + "/logo",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
}

/**
 * Sync a remote logo URL (e.g. the one MySA gives us) into Firestore without
 * re-uploading any binary. Cheaper option when the admin just wants both sides
 * to point at the existing HR logo.
 */
export async function setCompanyLogoUrl(company_id: string, logo_url: string) {
    return instanceAPI.put(
        config.api.url + apiConfig.base.company + "/" + company_id + "/logo-url",
        { logo_url },
    );
}
export async function generateQuestion(sliderValues, totalQuestion: number) {
    return instanceAPI.post(config.api.url + apiConfig.base.generate_question, { sliderValues: sliderValues, totalQuestion: Number(totalQuestion) });
}
export async function createInterview(input) {
    return instanceAPI.post(config.api.url + apiConfig.base.create_room + "/" + tokenService.getHR9CompanyUUID(), { input });
}


export async function putStandardAssessment(commpany_id, questionsBody) {
    return instanceAPI.put(config.api.url + apiConfig.base.std_assessment + "/" + commpany_id, { questions: questionsBody });
}
export async function updateStandardAssessment(commpany_id, question_id, question) {
    return instanceAPI.put(config.api.url + apiConfig.base.std_assessment + "/" + commpany_id + "/" + question_id, { question: question });
}
export async function deleteStandardAssessment(commpany_id, question_id) {
    return instanceAPI.delete(config.api.url + apiConfig.base.std_assessment + "/" + commpany_id + "/" + question_id);
}
export async function getStandardAssessment(commpany_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.std_assessment + "/" + commpany_id);
}

export async function addCandidate(body, job_question_id) {
    return instanceAPI.post(config.api.url + apiConfig.base.candidate + "/" + tokenService.getHR9CompanyUUID() + "/add/" + job_question_id, { input: body });
}

const CANDIDATE_PAGE_LIMIT = 200;
const CANDIDATE_FETCH_MAX_PAGES = 200;

export type FetchCandidatesProgressCallback = (loadedCount: number) => void;

/**
 * ดึงผู้สมัครทั้งหมดของ job (API แบ่งหน้า — รวมหลาย request อัตโนมัติ)
 * @param onProgress เรียกทุกครั้งที่รวมข้อมูลเพิ่มหนึ่งหน้า (จำนวนคนสะสม)
 */
export async function getAllCandidate(
    job_question_id: string,
    options?: { onProgress?: FetchCandidatesProgressCallback },
) {
    const companyId = tokenService.getHR9CompanyUUID();
    const baseUrl = config.api.url + apiConfig.base.candidate + "/" + companyId + "/" + job_question_id;
    const merged: unknown[] = [];
    let afterDocId: string | undefined;
    let lastResponse: { data: Record<string, unknown> } | null = null;
    const onProgress = options?.onProgress;

    for (let page = 0; page < CANDIDATE_FETCH_MAX_PAGES; page += 1) {
        let res;
        try {
            res = await instanceAPI.get(baseUrl, {
                params: {
                    limit: CANDIDATE_PAGE_LIMIT,
                    ...(afterDocId ? { after_doc_id: afterDocId } : {}),
                },
            });
        } catch (error: any) {
            const status = Number(error?.response?.status || 0);
            const msg = String(error?.response?.data?.error || "");
            // บาง job ไม่มีผู้สมัคร ACTIVE: backend ตอบ 404 ("No active candidates found ...")
            // สำหรับหน้ารวมผู้สมัคร ให้ตีความเป็นรายการว่างแทน error
            if (status === 404 && msg.toLowerCase().includes("no active candidates")) {
                return {
                    data: {
                        is_success: true,
                        data: [],
                        pagination: {
                            has_more: false,
                            next_after_doc_id: null,
                        },
                        message: "No active candidates",
                    },
                };
            }
            throw error;
        }
        lastResponse = res;
        if (!res.data?.is_success) {
            return res;
        }
        const chunk = Array.isArray(res.data.data) ? res.data.data : [];
        merged.push(...chunk);
        onProgress?.(merged.length);
        const pag = res.data.pagination as
            | { has_more?: boolean; next_after_doc_id?: string | null }
            | undefined;
        if (!pag?.has_more || !pag?.next_after_doc_id) {
            break;
        }
        afterDocId = pag.next_after_doc_id;
    }

    return {
        ...lastResponse,
        data: {
            ...(lastResponse?.data ?? {}),
            is_success: true,
            data: merged,
            message: "Candidates retrieved successfully",
        },
    };
}

export async function getCandidatePage(
    job_question_id: string,
    options?: {
        limit?: number;
        after_doc_id?: string;
        disable_custom_sort?: boolean;
        filter_status?: string;
        sort_by?: string;
    },
) {
    const companyId = tokenService.getHR9CompanyUUID();
    const baseUrl = config.api.url + apiConfig.base.candidate + "/" + companyId + "/" + job_question_id;
    const limit = options?.limit && Number.isFinite(options.limit) ? options.limit : 50;
    return instanceAPI.get(baseUrl, {
        params: {
            limit,
            ...(options?.after_doc_id ? { after_doc_id: options.after_doc_id } : {}),
            ...(options?.disable_custom_sort ? { disable_custom_sort: "true" } : {}),
            ...(options?.filter_status ? { filter_status: options.filter_status } : {}),
            ...(options?.sort_by ? { sort_by: options.sort_by } : {}),
        },
    });
}

export async function getAllCandidateByFilter(job_question_id, filter_status, filter_start_date, filter_end_date) {
    return instanceAPI.post(config.api.url + apiConfig.base.candidate + "/filter/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id,
        { filter_status: filter_status, filter_start_date: filter_start_date, filter_end_date: filter_end_date });
}

/** จำนวนผู้สมัครที่ลบ (DELETED) / ปฏิเสธ (REJECTED) ต่อตำแหน่ง */
export async function getNonActiveCandidateSummary(job_question_id: string) {
    const companyId = tokenService.getHR9CompanyUUID();
    return instanceAPI.get(
        `${config.api.url}${apiConfig.base.candidate}/${companyId}/${job_question_id}/non-active-summary`,
    );
}

/** รายชื่อผู้สมัคร DELETED หรือ REJECTED */
export async function getNonActiveCandidates(
    job_question_id: string,
    status: "DELETED" | "REJECTED",
    limit = 100,
) {
    const companyId = tokenService.getHR9CompanyUUID();
    return instanceAPI.get(
        `${config.api.url}${apiConfig.base.candidate}/${companyId}/${job_question_id}/non-active-candidates`,
        { params: { status, limit } },
    );
}
export async function getCandidateByID(job_question_id, candidate_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.candidate + "/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id + "/" + candidate_id);
}
export async function deleteCandidateByID(job_question_id, candidate_id) {
    return instanceAPI.delete(config.api.url + apiConfig.base.candidate + "/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id + "/" + candidate_id);
}

export async function getJD(job_title, job_exp, job_salary, job_level, industry, department, work_environment, employment_type) {
    const body = { job_title, job_exp, job_salary, job_level, industry, department, work_environment, employment_type };
    console.log(body);
    return instanceAPI.post(config.api.url + apiConfig.base.generate_jd, body);
}

export async function getDescription(title, description) {
    const body = { title, description };
    console.log(body);
    return instanceAPI.post(config.api.url + apiConfig.base.generate_description, body);
}

export async function postInterviewQuestion(body) {
    return instanceAPI.post(config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID(), body);
}

/** แก้ไขชุดคำถามทั่วไป (general) — backend อนุญาตเฉพาะเมื่อยังไม่มีผู้สมัคร ACTIVE */
export async function putInterviewQuestion(job_question_id: string, body: Record<string, unknown>) {
    return instanceAPI.put(
        config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id,
        body,
    );
}
export async function cleanCandidate(job_question_id) {
    return instanceAPI.post(config.api.url + "/admin/cleanup-deleted-candidates", { job_question_id: job_question_id });
}

// By-Pass Choose Language Settings API
export async function getEnglishInterviewIds(company_id) {
    return instanceAPI.get(config.api.url + "/settings/by-pass-choose-language/" + company_id);
}

export async function addEnglishInterviewId(company_id, job_question_id, language) {
    return instanceAPI.post(config.api.url + "/settings/by-pass-choose-language/" + company_id, { job_question_id, language });
}

export async function updateEnglishInterviewId(company_id, job_question_id, from_language, to_language) {
    return instanceAPI.put(config.api.url + "/settings/by-pass-choose-language/" + company_id + "/" + job_question_id, { from_language, to_language });
}

export async function deleteEnglishInterviewId(company_id, job_question_id, language) {
    return instanceAPI.delete(config.api.url + "/settings/by-pass-choose-language/" + company_id + "/" + job_question_id + "?language=" + language);
}

export async function getCompanyCredits(company_id: string) {
    return instanceAPI.get(config.api.url + "/settings/company-credits/" + company_id);
}

export async function updateCompanyCredits(
    company_id: string,
    body: {
        report_credit_total: number | null;
        template_question_credit_total: number | null;
        video_credit_total_minutes?: number | null;
    },
) {
    return instanceAPI.put(config.api.url + "/settings/company-credits/" + company_id, body);
}
export async function getInterviewQuestion(page = 1, limit = 10) {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
    });
    
    return instanceAPI.get(config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID() + "?" + params.toString());
}

export async function getInterviewQuestionByUserID(user_uuid, page = 1, limit = 10) {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
    });
    
    return instanceAPI.get(config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID() + "/id/"+ user_uuid + "?" + params.toString());
}

export async function getInterviewQuestionById(job_question_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id);
}
export async function getInterviewQuestionByIdAndUserID(job_question_id, user_uuid) {
    return instanceAPI.get(config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id +"/id/"+user_uuid);
}

/** Soft-archive: sets interview_questions.status to ARCHIVED (hidden from ACTIVE lists e.g. hr-landing) */
export async function deleteJobQuestionById(job_question_id) {
    return instanceAPI.delete(config.api.url + apiConfig.base.template_question + "/" + tokenService.getHR9CompanyUUID() + "/" + job_question_id);
}

// Get job list from Shanti
export async function getJobListFromShanti(company_id, params = {}) {
    // Use different base URL for this specific API in production
    const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://interview-internal-b2hya4w4ka-as.a.run.app/api'
        : config.api.url;
    
    return instanceAPI.post(`${baseUrl}/admin/jobs/list`, {
        company_id,
        ...params
    });
}

// Check if job-id-shanti is already mapped to a job-question
export async function checkJobIdShantiExists(company_id, job_id_shanti) {
    return instanceAPI.get(
        config.api.url + apiConfig.base.template_question + 
        `/check-by-job-id/${company_id}/${job_id_shanti}`
    );
}

// Update job-id-shanti for a job-question (1 job-question = 1 job-id-shanti)
export async function updateJobIdShanti(company_id, job_question_id, jobIdShanti) {
    // Use different base URL for this specific API in production
    const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://interview-internal-b2hya4w4ka-as.a.run.app/api'
        : config.api.url;
    
    return instanceAPI.put(
        `${baseUrl}${apiConfig.base.template_question}/${company_id}/${job_question_id}/job-id-shanti`,
        { 'job-id-shanti': jobIdShanti }
    );
}

// Remove job-id-shanti from a job-question
export async function removeJobIdShanti(company_id, job_question_id) {
    // Use different base URL for this specific API in production
    const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://interview-internal-b2hya4w4ka-as.a.run.app/api'
        : config.api.url;
    
    return instanceAPI.delete(
        `${baseUrl}${apiConfig.base.template_question}/${company_id}/${job_question_id}/job-id-shanti`
    );
}

export async function getReportById(report_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.report + "/" + report_id);
}

/** POST /hr9/report-score - get or generate report score (match_score, strengths, weaknesses, next_steps). Same baseUrl as Shanti. */
export async function getReportScore(report_id, job) {
    return instanceAPI.post(config.api.url + "/hr9/report-score", { report_id, job });
}

/** POST /custom-measure-score — วิเคราะห์และบันทึก custom_measure_analysis; force=true รันใหม่ทับของเดิม */
export async function postCustomMeasureScore(report_id: string, opts?: { force?: boolean }) {
    return instanceAPI.post(config.api.url + "/custom-measure-score", {
        report_id,
        ...(opts?.force ? { force: true } : {}),
    });
}

/** POST /general-objective-analysis — ชุด General: วิเคราะห์คำตอบเทียบ Objective (Generative AI) */
export async function postGeneralObjectiveAnalysis(report_id: string, opts?: { force?: boolean }) {
    return instanceAPI.post(config.api.url + "/general-objective-analysis", {
        report_id,
        ...(opts?.force ? { force: true } : {}),
    });
}

/** POST /custom-measure-score-compare — admin: รัน Gemini + OpenAI พร้อมกัน ไม่เขียน Firestore */
export async function postCustomMeasureCompareScore(report_id: string) {
    return instanceAPI.post(config.api.url + "/custom-measure-score-compare", {
        report_id,
    });
}

/** PUT เกณฑ์การวัดผลร่วมของ job question — ใช้กับทุก candidate */
export async function putJobCustomMeasurementCriteria(
    job_question_id: string,
    custom_measurement_criteria: string,
) {
    return instanceAPI.put(
        `${config.api.url}${apiConfig.base.template_question}/${tokenService.getHR9CompanyUUID()}/${job_question_id}/custom-measurement-criteria`,
        { custom_measurement_criteria },
    );
}

/** GET /report/profi-snapshot/:report_id — ProFi analysis (interview_report_profi READY) */
export async function getProfiReportSnapshot(report_id: string) {
    return instanceAPI.get(`${config.api.url}/report/profi-snapshot/${report_id}`);
}

/** POST /report/force-regenerate/:report_id — route ตาม app_id / ประเภทชุด (PROFI, General, Custom Insight, Report Score) */
export async function postForceRegenerateReport(
    report_id: string,
    opts?: { force?: boolean; send_email?: boolean; job?: object | null },
) {
    return instanceAPI.post(`${config.api.url}/report/force-regenerate/${report_id}`, {
        force: opts?.force !== false,
        ...(opts?.send_email ? { send_email: true } : {}),
        ...(opts?.job ? { job: opts.job } : {}),
    });
}

/** POST /report/profi-reanalyze/:report_id — รัน ProFi analyze ใหม่ (ไม่ส่งอีเมล candidate) */
export async function postProfiReanalyze(
    report_id: string,
    opts?: { force?: boolean; send_email?: boolean; allow_incomplete?: boolean },
) {
    return instanceAPI.post(`${config.api.url}/report/profi-reanalyze/${report_id}`, {
        ...(opts?.force ? { force: true } : {}),
        ...(opts?.send_email ? { send_email: true } : {}),
        ...(opts?.allow_incomplete ? { allow_incomplete: true } : {}),
    });
}

export async function checkReportMultipleChoice(report_id, job_question_id, candidate_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.report + "/check-multiplechoice/" + report_id + "/" + job_question_id + "/" + candidate_id);
}

export async function UnlockReport(report_id, company_id, candidate_id, candidate_name) {
    const body = {
        company_id, candidate_id, candidate_name
    }
    return instanceAPI.put(config.api.url + apiConfig.base.report + "/use-credit/" + report_id, body);
}

export async function getAllResumes(company_id, job_question_id) {

    return instanceAPI.post(config.api.url + apiConfig.base.report + "/all-resumes", { company_id, job_question_id });
}

export async function updateUser(company_id, input) {
    console.log(input);
    return instanceAPI.patch(config.api.url + apiConfig.base.update_user_management + "/" + company_id, { input });
}

export async function getUserList(company_id) {
    return instanceAPI.get(config.api.url + apiConfig.base.get_user_management + "/" + company_id);
}


export async function addUsersToJob(company_id, job_question_id, users) {
    return instanceAPI.put(config.api.url + apiConfig.base.add_user_to_job + "/" + company_id + "/"+ job_question_id, {users}) ;
}
export async function getAllInsightFilterResume(company_id, job_question_id) {

    return instanceAPI.post(config.api.url + apiConfig.base.report + "/filter-resumes", { company_id, job_question_id });
}
export async function genQuestionSoftSkill(input, totalQuestion) {
    const body = {
        input, totalQuestion, tone: input.tone
    }
    return instanceQuizAPI.post(config.quiz.url + apiConfig.base.interview_question + "/soft-skill", body);
}

export async function genAssessmentQuestion(title, description, question_type) {
    const body = {
        title, description, question_type
    }
    return instanceQuizAPI.post(config.quiz.url + apiConfig.base.assessment_question + "/question", body);
}
export async function genQuestionByCompanyUrl(company_url, totalQuestion) {
    const body = {
        company_url, totalQuestion
    }
    return instanceQuizAPI.post(config.quiz.url + apiConfig.base.interview_question + "/company-question", body);
}
export async function sendEmailSchedule(body) {

    return instanceAPI.post(config.api.url + apiConfig.base.send_email, body);
}

export async function detectAI(report_id) {
    return instanceAPI.post(config.api.url + apiConfig.base.report + "/detect-ai/" + report_id);
}

export async function searchCreditLog(start_date, end_date, type?: "REPORT" | "TEMPLATE") {
    const params = new URLSearchParams();
    params.set("start_date", start_date || "");
    params.set("end_date", end_date || "");
    if (type) params.set("type", type);

    // Report credit history should reflect COMPLETED & unlocked reports (source-of-truth: interview_report)
    if (String(type || "").toUpperCase() === "REPORT") {
        const p2 = new URLSearchParams();
        const s = String(start_date || "").trim();
        const e = String(end_date || "").trim();
        if (s) p2.set("start_date", s);
        if (e) p2.set("end_date", e);
        p2.set("limit", "200"); // admin UI loads all for the selected range
        p2.set("include_job_name", "true");
        return instanceAPI.get(
            `${config.api.url}/credits/report-history/search/${tokenService.getHR9CompanyUUID()}?${p2.toString()}`,
        );
    }

    return instanceAPI.get(
        config.api.url + apiConfig.base.credit_log + "/" + tokenService.getHR9CompanyUUID() + "?" + params.toString()
    );
}
export async function genQuestionHardSkill(input, totalQuestion) {
    const body = {
        input, totalQuestion, tone: input.tone
    }
    return instanceQuizAPI.post(config.quiz.url + apiConfig.base.interview_question + "/hard-skill", body);
}

export async function genQuestionBehavior(input, totalQuestion) {
    const body = {
        input, totalQuestion, tone: input.tone
    }
    return instanceQuizAPI.post(config.quiz.url + apiConfig.base.interview_question + "/behavior", body);
}

export async function postEvaluationTest(body): Promise<any> {

    try {

        return axios.post(config.quiz.url + apiConfig.base.interview_question, body, {
            headers: {
                'Content-Type': "application/json",
            }
        }).then(res => res.data as any).catch(err => Promise.reject(err))

    } catch (error: any) {
        throw new Error(`Failed to update user status: ${error.message}`);
    }
}


export async function uploadResume(fileUpload, filename): Promise<UploadResumeModel> {
    try {
        const formData = new FormData();
        formData.append("resume", fileUpload, filename);
        formData.append("type", "interview_ai");
        return axios.post(config.upload_resume.base_url + "/resume/upload", formData, {
            headers: {
                "x-api-key": config.upload_resume.x_api_key
            }
        }).then(res => res.data as UploadResumeModel).catch(err => Promise.reject(err))

    } catch (error: any) {
        throw new Error(`Failed to update user status: ${error.message}`);
    }
}
export async function getSummaryResume(resume_key): Promise<SummaryResumeModel> {
    try {
        const body = {
            resume_key: resume_key
        }
        return axios.post(config.upload_resume.lambda_url + "resume-interview" + "/resume/summary", body, {
            headers: {
                "x-api-key": config.upload_resume.x_api_key
            }
        }).then(res => res.data as SummaryResumeModel).catch(err => Promise.reject(err))

    } catch (error: any) {
        throw new Error(`Failed to update user status: ${error.message}`);
    }
}
export async function getResume(resume_key): Promise<void> {
    try {

        return axios.get(config.upload_resume.lambda_url + "resume-interview" +
            "/resume?resume_key=" + resume_key, {
            headers: {
                "x-api-key": config.upload_resume.x_api_key
            }
        }).then(res => res.data as void).catch(err => Promise.reject(err))

    } catch (error: any) {
        throw new Error(`Failed to update user status: ${error.message}`);
    }
}


export async function downloadResume(bucket: string, key: string, user_name: string, candidate_id) {

    return axios.get(config.upload_resume.base_url + "?" + new URLSearchParams({
        bucket: bucket,
        key: key,
        user_name: user_name,
        candidate_id: candidate_id
    }), {
        method: 'GET', headers: {
            "x-api-key": config.upload_resume.x_api_key
        }
    }
    ).then(res => res.data as void).catch(err => Promise.reject(err))
}
export async function getAllSkills(): Promise<SkillsModel> {
    try {
        return axios.get(config.skills.url + apiConfig.skills.skill, {
            headers: {
                'Content-Type': "application/json",
                "x-api-key": config.skills.x_api_key
            }
        }).then(res => res.data as SkillsModel).catch(err => Promise.reject(err))
    } catch (error: any) {
        throw new Error(`Failed to update user status: ${error.message}`);
    }
}

// Insight Dashboard API Functions
export async function getInsightDashboard(company_id, job_question_id) {
    const body = {
        company_id
    }
    return instanceAPI.post(config.api.url + apiConfig.base.report + "/insight-dashboard/" + job_question_id, body);
}

export async function getPerformanceAnalytics(company_id, job_question_id, date_range = null) {
    const body = {
        company_id,
        date_range 
    }
    return instanceAPI.post(config.api.url + apiConfig.base.report + "/performance-analytics/" + job_question_id, body);
}

export async function getRealtimeAnalytics(company_id, job_question_id) {
    const body = {
        company_id
    }
    return instanceAPI.post(config.api.url + apiConfig.base.report + "/realtime-analytics/" + job_question_id, body);
}

/** สรุปภาพรวม custom_measure_analysis ต่อ job question (admin Custom Insight dashboard) */
export async function getCustomMeasureDashboard(company_id: string, job_question_id: string) {
    return instanceAPI.post(
        config.api.url + apiConfig.base.report + "/custom-measure-dashboard/" + job_question_id,
        { company_id },
    );
}

/** AI สรุปภาพรวม + เหตุผลจัดอันดับ (ใช้ OPEN_AI_API_KEY ฝั่ง server) */
export async function postCustomMeasureDashboardAiInsight(company_id: string, job_question_id: string) {
    return instanceAPI.post(
        config.api.url + apiConfig.base.report + "/custom-measure-dashboard-ai-insight/" + job_question_id,
        { company_id },
    );
}

/** เชิญผู้ใช้ HR (Firebase email/password) — Leela JWT หรือ Firebase admin (hr-landing) */
export async function inviteHrFirebaseUser(
    company_id: string,
    body: {
        email: string;
        display_name?: string;
        role?: string;
        contributor_group_id?: string;
        hide_from_hr_ui?: boolean;
    },
) {
    return instanceAPI.post(`${config.api.url}/hr-firebase/${company_id}/invite`, body);
}

export async function listHrFirebaseUsers(company_id: string) {
    return instanceAPI.get(`${config.api.url}/hr-firebase/${company_id}/users`);
}

/** นำผู้ใช้ HR ออกจากองค์กร (ลบ membership) — ต้องล็อกอิน Leela */
export async function removeHrFirebaseUserFromCompany(company_id: string, uid: string) {
    return instanceAPI.delete(
        `${config.api.url}/hr-firebase/${encodeURIComponent(company_id)}/users/${encodeURIComponent(uid)}`,
    );
}

export async function updateHrFirebaseUserHideFromHrUi(
    company_id: string,
    uid: string,
    hide_from_hr_ui: boolean,
) {
    return instanceAPI.patch(
        `${config.api.url}/hr-firebase/${encodeURIComponent(company_id)}/users/${encodeURIComponent(uid)}/hide-from-hr-ui`,
        { hide_from_hr_ui },
    );
}

